export interface InspectContent {
  title: string;
  lines: string[];
  imageUrl?: string;
  imageAlt?: string;
}

export interface Inspector {
  element: HTMLElement;
  open(content: InspectContent): void;
  close(): void;
  isOpen(): boolean;
}

/**
 * Zoom d'inspection : une modale légère pour lire une affiche, une pancarte
 * ou une ardoise sans quitter le décor.
 */
export function createInspector(parent: HTMLElement): Inspector {
  const element = document.createElement('div');
  element.className = 'inspect';
  element.setAttribute('role', 'dialog');
  element.setAttribute('aria-modal', 'true');
  element.hidden = true;

  const card = document.createElement('div');
  card.className = 'inspect__card';

  const title = document.createElement('h2');
  title.className = 'inspect__title';

  const media = document.createElement('div');
  media.className = 'inspect__media';

  const text = document.createElement('div');
  text.className = 'inspect__text';

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'inspect__close';
  close.setAttribute('aria-label', 'Fermer l’inspection');
  close.textContent = '×';

  card.append(close, title, media, text);
  element.append(card);
  parent.append(element);

  let lastFocused: HTMLElement | null = null;

  function hide(): void {
    element.hidden = true;
    element.classList.remove('is-open');
    lastFocused?.focus();
    lastFocused = null;
  }

  function open(content: InspectContent): void {
    title.textContent = content.title;
    element.setAttribute('aria-label', content.title);

    media.replaceChildren();
    if (content.imageUrl) {
      const image = document.createElement('img');
      image.src = content.imageUrl;
      image.alt = content.imageAlt ?? '';
      image.addEventListener('error', () => image.remove());
      media.append(image);
    }

    text.replaceChildren();
    for (const line of content.lines) {
      const paragraph = document.createElement('p');
      paragraph.textContent = line;
      text.append(paragraph);
    }

    lastFocused = document.activeElement as HTMLElement | null;
    element.hidden = false;
    requestAnimationFrame(() => element.classList.add('is-open'));
    close.focus();
  }

  close.addEventListener('click', hide);
  element.addEventListener('click', (event) => {
    if (event.target === element) hide();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !element.hidden) hide();
  });

  return { element, open, close: hide, isOpen: () => !element.hidden };
}
